import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './file-embed.module.scss'
import cn from 'classnames'
import BabylonViewer from './BabylonViewer'
import { Rotate3D } from 'lucide-react'
import { getCachedAnswer } from 'state/cache'

type FileType = {
    name: string
    url: string
    type: string
    size: number
    id?: string
    content_type?: string
    preview_url?: string
    preview_urls?: Record<string, string>
}

interface Props {
    file: FileType
    height?: number | string
    className?: string
    onPreviewGenerated?: (blob: Blob, dataUrl: string, aspect?: string) => void
    onPreviewProgress?: (percent: number) => void
    onPreviewCaptureStart?: () => void
    onPreviewCaptureEnd?: () => void
    localUrl?: string
    answerId?: number | string
    autoCapturePreview?: boolean
}

export default function ThreeDModelLoader({
    file,
    height,
    className,
    onPreviewGenerated,
    onPreviewProgress,
    onPreviewCaptureStart,
    onPreviewCaptureEnd,
    localUrl,
    answerId,
    autoCapturePreview = false,
}: Props) {
    const previewUrls = file.preview_urls
    const preview1x1 = previewUrls?.['1x1'] || file.preview_url
    const [forceModel, setForceModel] = useState<boolean>(false)
    const [modelStarted, setModelStarted] = useState<boolean>(false)
    const [previewFailed, setPreviewFailed] = useState<boolean>(false)
    const startedRef = useRef(false)
    const effectivePreviewUrl = previewFailed ? undefined : preview1x1
    const showPreview = !!effectivePreviewUrl && !forceModel && !modelStarted
    const showPlaceholder = !effectivePreviewUrl && !forceModel && !modelStarted
    const canCapturePreview = Boolean(onPreviewGenerated)
    const autoCapturePendingRef = useRef(false)
    const autoCaptureTimeoutRef = useRef<number | null>(null)
    const onPreviewCaptureStartRef = useRef(onPreviewCaptureStart)
    const onPreviewCaptureEndRef = useRef(onPreviewCaptureEnd)
    const modelFile = useMemo(
        () => (showPreview ? { ...file, preview_url: effectivePreviewUrl } : { ...file }),
        [file, showPreview, effectivePreviewUrl],
    )

    useEffect(() => {
        onPreviewCaptureStartRef.current = onPreviewCaptureStart
        onPreviewCaptureEndRef.current = onPreviewCaptureEnd
    }, [onPreviewCaptureStart, onPreviewCaptureEnd])

    const clearAutoCaptureTimeout = useCallback(() => {
        if (!autoCaptureTimeoutRef.current) return
        window.clearTimeout(autoCaptureTimeoutRef.current)
        autoCaptureTimeoutRef.current = null
    }, [])

    const finishAutoCapture = useCallback(() => {
        if (!autoCapturePendingRef.current) return
        autoCapturePendingRef.current = false
        clearAutoCaptureTimeout()
        onPreviewCaptureEndRef.current?.()
    }, [clearAutoCaptureTimeout])

    useEffect(() => {
        startedRef.current = false
        setForceModel(false)
        setModelStarted(false)
        setPreviewFailed(false)
    }, [file.id, file.url, file.name])

    useEffect(() => {
        if (preview1x1) {
            setPreviewFailed(false)
        }
    }, [preview1x1])

    const startModel = useCallback(() => {
        setForceModel(true)
        startedRef.current = true
        setModelStarted(true)
    }, [])

    const startAutoCapture = useCallback(() => {
        if (!autoCapturePendingRef.current) {
            autoCapturePendingRef.current = true
            onPreviewCaptureStartRef.current?.()
        }

        clearAutoCaptureTimeout()
        autoCaptureTimeoutRef.current = window.setTimeout(() => {
            console.warn('ThreeDModelLoader: preview capture timed out', {
                fileName: file?.name,
                fileUrl: file?.url,
            })
            finishAutoCapture()
        }, 45000)

        startModel()
    }, [clearAutoCaptureTimeout, file?.name, file?.url, finishAutoCapture, startModel])

    useEffect(() => {
        if (
            !autoCapturePreview ||
            !canCapturePreview ||
            effectivePreviewUrl ||
            forceModel ||
            modelStarted
        )
            return
        startAutoCapture()
    }, [
        autoCapturePreview,
        canCapturePreview,
        effectivePreviewUrl,
        forceModel,
        modelStarted,
        startAutoCapture,
    ])

    useEffect(() => {
        if (effectivePreviewUrl) finishAutoCapture()
    }, [effectivePreviewUrl, finishAutoCapture])

    useEffect(() => {
        return () => {
            finishAutoCapture()
            clearAutoCaptureTimeout()
        }
    }, [clearAutoCaptureTimeout, finishAutoCapture])

    return (
        <div
            className={cn(styles.modelLoader, className)}
            style={height ? { height } : undefined}
            data-post-embed-navigation-exempt="true"
        >
            {showPreview ? (
                <>
                    <img
                        src={effectivePreviewUrl}
                        className={styles.modelPreviewImage}
                        alt="model preview"
                        onError={event => {
                            const imageEl = event.currentTarget
                            const details: Record<string, unknown> = {
                                fileName: file?.name,
                                previewUrl: effectivePreviewUrl,
                                currentSrc: imageEl?.currentSrc,
                                timestamp: new Date().toISOString(),
                            }

                            const nativeEvent = event?.nativeEvent as Event & {
                                message?: string
                                path?: Array<EventTarget>
                            }
                            if (nativeEvent?.message) details.message = nativeEvent.message
                            if (nativeEvent?.type) details.nativeType = nativeEvent.type

                            console.error(
                                'ThreeDModelLoader: preview image failed to load',
                                details,
                            )

                            if (localUrl && imageEl.dataset.fallbackLocal !== 'true') {
                                imageEl.dataset.fallbackLocal = 'true'
                                imageEl.src = localUrl
                                return
                            }

                            if (answerId && imageEl.dataset.fallbackCached !== 'true') {
                                const cached = getCachedAnswer(String(answerId))
                                const cachedPreview = cached?.file_urls?.find(
                                    u => u?.id === file?.id || u?.attachment_id === file?.id,
                                )?.preview_url
                                if (cachedPreview && cachedPreview !== imageEl.currentSrc) {
                                    imageEl.dataset.fallbackCached = 'true'
                                    imageEl.src = cachedPreview
                                    return
                                }
                            }

                            setPreviewFailed(true)
                        }}
                    />
                    <button
                        type="button"
                        className={styles.modelPreviewCenterBtn}
                        title="Load 3D model"
                        onClick={ev => {
                            ev.stopPropagation()
                            startModel()
                        }}
                        onMouseDown={ev => {
                            ev.stopPropagation()
                        }}
                        onPointerDown={ev => {
                            ev.stopPropagation()
                            startModel()
                        }}
                        onTouchStart={ev => {
                            ev.stopPropagation()
                            ev.preventDefault()
                            startModel()
                        }}
                    >
                        <Rotate3D size={24} />
                    </button>
                </>
            ) : showPlaceholder ? (
                <div className={styles.modelPlaceholder}>
                    <button
                        type="button"
                        className={styles.modelPreviewCenterBtn}
                        title="Load 3D model"
                        onClick={ev => {
                            ev.stopPropagation()
                            startModel()
                        }}
                        onMouseDown={ev => {
                            ev.stopPropagation()
                        }}
                        onPointerDown={ev => {
                            ev.stopPropagation()
                            startModel()
                        }}
                        onTouchStart={ev => {
                            ev.stopPropagation()
                            ev.preventDefault()
                            startModel()
                        }}
                    >
                        <Rotate3D size={24} />
                    </button>
                    <span className={styles.modelPlaceholderHint}>Load 3D model</span>
                </div>
            ) : (
                <BabylonViewer
                    file={modelFile}
                    height={height}
                    className={className}
                    onPreviewProgress={onPreviewProgress}
                    capturePreview={!effectivePreviewUrl && canCapturePreview}
                    captureAspects={['1x1']}
                    captureResolution={2}
                    onPreviewGenerated={async (blob, dataUrl) => {
                        try {
                            if (onPreviewGenerated) await onPreviewGenerated(blob, dataUrl, '1x1')
                        } finally {
                            finishAutoCapture()
                        }
                    }}
                    onPreviewFailed={finishAutoCapture}
                    preferLocalUrl={localUrl}
                />
            )}
        </div>
    )
}
